/**
 * Defaults, one-off data clean-ups, diagnostics and the selective resets.
 *
 * The clean-ups go through `updateClients` / `updateCrm`, which mark only the
 * records that actually changed — so "normalise phone numbers" on a book of
 * 400 clients sends the handful it fixed, not all 400.
 */
import { useApp } from '../../store/AppContext.jsx';
import { formatDate } from '../../lib/dates.js';
import { planAmount, isBilled } from '../../domain/pricing.js';
import { nextDueOf } from '../../domain/derive.js';
import { nextOwner } from '../../services/automation.js';
import { Section, Grid, Row, NumberField, SelectField } from './SettingsBits.jsx';

const START_VIEWS = [
  ['dashboard', 'Client dashboard'],
  ['sales', 'Sales overview'],
  ['pipeline', 'Pipeline'],
  ['clients', 'Clients'],
  ['queue', 'Needs Action'],
  ['billing', 'Billing'],
  ['outbox', 'Outbox']
];

const DATE_FORMATS = [['long', '05 May 2026'], ['iso', '2026-05-05'], ['slash', '05/05/2026']];

const KEYBOARD = [
  ['Esc', 'Close any panel or dialog'],
  ['Ctrl / Cmd + Enter', 'Save the open record'],
  ['Click a stat tile', 'Filter the list by it'],
  ['Drag a deal card', 'Move it between stages']
];

/** Normalise to +971… — the local formats people paste vary wildly. */
function normalisePhone(value) {
  let d = String(value || '').replace(/[^0-9]/g, '');
  if (!d) return value;
  if (d.startsWith('00')) d = d.slice(2);
  if (d.startsWith('0')) d = `971${d.slice(1)}`;
  if (!d.startsWith('971') && d.length <= 9) d = `971${d}`;
  return `+${d}`;
}

export default function AdvancedPane() {
  const {
    S, rows, crm, alerts, derive, updateClients, updateCrm, replaceClients, replaceCrm,
    patchSettings, clearAlertHistory, toast
  } = useApp();

  const counts = {
    Clients: rows.length,
    Deals: crm.deals.length,
    Contacts: crm.contacts.length,
    Companies: crm.companies.length,
    Activities: crm.acts.length,
    Drafts: rows.filter(r => r.outbox).length,
    Alerts: alerts.length,
    Rules: (S.rules || []).length
  };

  const sizeOf = o => {
    try { return `${(JSON.stringify(o).length / 1024).toFixed(1)} KB`; } catch { return '—'; }
  };

  /* ---- data hygiene ---- */

  const renumber = () => {
    let n = 0;
    updateClients(r => {
      const sno = ++n;
      return r.sno === sno ? r : { ...r, sno };
    });
    toast(`Renumbered ${rows.length} clients`);
  };

  const normalisePhones = () => {
    let n = 0;
    const fix = record => {
      if (!record.phone) return record;
      const phone = normalisePhone(record.phone);
      if (phone === record.phone) return record;
      n += 1;
      return { ...record, phone };
    };
    updateClients(fix);
    updateCrm('contacts', fix);
    toast(`${n} numbers normalised`);
  };

  const findDuplicates = () => {
    const seen = new Set();
    const dupes = [];
    rows.forEach(r => {
      const key = String(r.email || r.name || '').toLowerCase().trim();
      if (!key) return;
      if (seen.has(key)) dupes.push(r.name); else seen.add(key);
    });
    toast(dupes.length
      ? `${dupes.length} possible duplicates: ${dupes.slice(0, 3).join(', ')}`
      : 'No duplicates found');
  };

  const assignUnowned = () => {
    let rrIndex = S.rrIndex || 0;
    const take = () => {
      const next = nextOwner({ ...S, rrIndex });
      rrIndex = next.rrIndex;
      return next.owner;
    };

    const clients = updateClients(r =>
      (String(r.handler || '').trim() || derive(r).excluded ? r : { ...r, handler: take() }));
    const deals = updateCrm('deals', d =>
      (d.owner || d.stage === 'Won' || d.stage === 'Lost' ? d : { ...d, owner: take() }));

    patchSettings({ rrIndex });
    toast(`${clients + deals} records assigned`);
  };

  const fillAmounts = () => {
    const n = updateClients(r =>
      (r.amount || !isBilled(S, r) ? r : { ...r, amount: String(planAmount(S, r)) }));
    toast(`${n} amounts filled from plan pricing`);
  };

  const rebuildDueDates = () => {
    const n = updateClients(r => {
      if (r.nextDue || !isBilled(S, r)) return r;
      const due = nextDueOf(S, r);
      return due ? { ...r, nextDue: formatDate(due, false, S.dateFmt) } : r;
    });
    toast(`${n} due dates rebuilt`);
  };

  const copyDiagnostics = () => {
    const text = JSON.stringify({ when: new Date().toISOString(), counts, settings: S }, null, 2);
    if (!navigator.clipboard) { toast('Clipboard unavailable'); return; }
    navigator.clipboard.writeText(text).then(() => toast('Diagnostics copied'), () => toast('Copy failed'));
  };

  /* ---- selective resets ---- */

  const clearClients = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete every client record? The pipeline is untouched.')) return;
    replaceClients([]);
    toast('Clients cleared');
  };

  const clearPipeline = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete every deal, contact and company? Clients are untouched.')) return;
    replaceCrm({ companies: [], contacts: [], deals: [], acts: [] });
    toast('Pipeline cleared');
  };

  return (
    <>
      <Section title="Defaults" hint="How the workspace opens and how dense it feels.">
        <Grid>
          <SelectField label="Open on" name="startView" options={START_VIEWS} />
          <SelectField label="Table or cards" name="startMode" options={[['list', 'Table'], ['grid', 'Cards']]} />
          <SelectField
            label="Row density"
            name="density"
            options={[['comfortable', 'Comfortable'], ['compact', 'Compact']]}
          />
          <SelectField
            label="Date format"
            name="dateFmt"
            options={DATE_FORMATS}
            hint="existing dates keep parsing either way"
          />
          <NumberField
            label="Queue rows on the dashboard"
            name="dashRows"
            min={3}
            max={50}
            unit="items in the dashboard queue — the table below shows every client"
          />
          <NumberField label="Queue length" name="queueMax" unit="items in Needs Action" min={5} max={200} />
        </Grid>
      </Section>

      <Section title="Data hygiene" hint="One-off clean-ups across every record. Take a backup first.">
        <Row>
          <button type="button" className="btn" onClick={renumber}>Renumber S.No</button>
          <button type="button" className="btn" onClick={normalisePhones}>Normalise phone numbers</button>
          <button type="button" className="btn" onClick={findDuplicates}>Find duplicate clients</button>
          <button type="button" className="btn" onClick={assignUnowned}>Assign unowned round-robin</button>
          <button type="button" className="btn" onClick={fillAmounts}>Fill missing amounts from plan</button>
          <button type="button" className="btn" onClick={rebuildDueDates}>Rebuild missing due dates</button>
        </Row>
      </Section>

      <Section title="AI tuning">
        <Grid>
          <NumberField
            label="Response length cap"
            name="aiTokens"
            unit="max tokens per reply"
            min={300}
            max={4000}
          />
          <NumberField
            label="Clients sent for context"
            name="aiRows"
            unit="caps the payload on large books"
            min={10}
            max={500}
          />
        </Grid>
      </Section>

      <Section title="Diagnostics" hint="What is stored right now.">
        <table className="mini">
          <thead><tr><th>Record</th><th className="r">Count</th></tr></thead>
          <tbody>
            {Object.entries(counts).map(([k, v]) => (
              <tr key={k}><td>{k}</td><td className="r">{v}</td></tr>
            ))}
          </tbody>
        </table>
        <p style={{ marginTop: 9 }}>
          Payload sizes — clients {sizeOf(rows)} · pipeline {sizeOf(crm)} · settings {sizeOf(S)}
        </p>
        <Row>
          <button type="button" className="btn" onClick={copyDiagnostics}>Copy diagnostics</button>
        </Row>
      </Section>

      <Section title="Keyboard">
        <table className="mini">
          <tbody>
            {KEYBOARD.map(([k, v]) => (
              <tr key={k}><td><b>{k}</b></td><td>{v}</td></tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section danger title="Selective reset" hint="Wipe one store without touching the others.">
        <Row>
          <button type="button" className="btn danger" onClick={clearClients}>Clear clients</button>
          <button type="button" className="btn danger" onClick={clearPipeline}>Clear pipeline</button>
          <button type="button" className="btn danger" onClick={clearAlertHistory}>Clear alert history</button>
        </Row>
      </Section>
    </>
  );
}
